import {useEffect, useState} from 'react';
import {Paginator} from 'src/design/organisms/Pagination';

import {Box} from 'src/design/atoms/Box';
import {Space} from 'src/design/atoms/Space';
import type {SourceEntityRecord} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetDepartmentListQuery} from 'src/redux/queries/department';

export interface DepartmentListProps {
  selected: SourceEntityRecord[];
  setSelected: React.Dispatch<React.SetStateAction<SourceEntityRecord[]>>;
  showOnlySelected: boolean;
  showSelectedAll: boolean;
  search?: string;
  itemsPerPage?: number;
  /** Callback for unfiltered total count (used for percentage calculation) */
  onTotalCountChange?: (count: number) => void;
}

export const DepartmentList: React.FC<DepartmentListProps> = ({
  selected,
  setSelected,
  showOnlySelected,
  showSelectedAll,
  search,
  itemsPerPage = 25,
  onTotalCountChange,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const selectedIds = selected.map(i => Number(i.entityId));

  // Baseline query for unfiltered total count (used for percentage calculation)
  const {data: baselineData} = useGetDepartmentListQuery({
    start: 0,
    length: 0,
  });

  // Filtered query for display and pagination
  const {
    data: departments,
    isFetching: isDepartmentsFetching,
    isLoading: isDepartmentsLoading,
  } = useGetDepartmentListQuery({
    search: search,
    start: currentPage * itemsPerPage - itemsPerPage,
    length: itemsPerPage,
    whitelistIds: showOnlySelected ? selectedIds : undefined,
  });

  const isLoading = isDepartmentsFetching || isDepartmentsLoading;

  // Report unfiltered total count for percentage calculation
  useEffect(() => {
    if (onTotalCountChange && baselineData?.totalRecords !== undefined) {
      onTotalCountChange(baselineData.totalRecords);
    }
  }, [baselineData?.totalRecords, onTotalCountChange]);

  function isSelected(id: number): boolean {
    return (
      selected.filter(element => element.entityId.toString() === id.toString())
        .length > 0 || showSelectedAll
    );
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const {checked} = e.target;
    if (checked) {
      setSelected([
        ...selected,
        {
          entityId: id,
          entityType: EntityTypeEnum.Department,
        },
      ]);
    } else {
      setSelected(selected.filter(i => i.entityId !== id));
    }
  };

  const handlePageChange = (_: unknown, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box>
      <Paginator
        mt={8}
        mb={16}
        currentPage={currentPage}
        totalRecords={departments?.totalRecords ?? 0}
        perPage={itemsPerPage}
        showRecordCount={false}
        onChange={handlePageChange}
      />
      {!isLoading ? (
        departments?.data ? (
          departments.data.map(item => (
            <CheckboxRow
              key={item.id}
              id={String(item.id)}
              label={`${item.code} - ${item.description}`}
              checked={isSelected(item.id)}
              onChange={e => handleSelect(e, Number(item.id))}
              disabled={showSelectedAll}
            />
          ))
        ) : (
          <Box mt="lg">No selections found</Box>
        )
      ) : (
        <>
          <TableBodyRender isLoading={isLoading} />
          <Space mb="xl" />
        </>
      )}
    </Box>
  );
};
